import React, { useState, useEffect } from "react";
import { 
  collection, 
  addDoc, 
  getDocs, 
  deleteDoc, 
  doc, 
  updateDoc
} from "firebase/firestore";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../firebase";
import DashboardBase from "../DashboardBase";

const UsuariosManager = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUsuario, setEditingUsuario] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterEmpresa, setFilterEmpresa] = useState("");
  const [filterRol, setFilterRol] = useState("");
  const [groupBy, setGroupBy] = useState("none"); // "none", "empresa", "rol"
  const [formData, setFormData] = useState({
    nombre: "",
    apellido: "",
    email: "",
    password: "",
    cedula: "",
    telefono: "",
    rol: "evaluador",
    empresaId: "",
    estado: "activo"
  });

  // Cargar datos
  const loadData = async () => {
    try {
      // Cargar usuarios
      const usuariosSnapshot = await getDocs(collection(db, "usuarios"));
      const usuariosData = usuariosSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Cargar empresas
      const empresasSnapshot = await getDocs(collection(db, "empresas"));
      const empresasData = empresasSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setUsuarios(usuariosData);
      setEmpresas(empresasData);
    } catch (error) {
      console.error("Error al cargar datos:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Obtener nombre de empresa
  const getEmpresaNombre = (empresaId) => {
    const empresa = empresas.find(e => e.id === empresaId);
    return empresa ? empresa.nombre : "Sin empresa";
  };

  // Filtrar usuarios basado en los criterios de búsqueda
  const getFilteredUsuarios = () => {
    let filtered = usuarios.filter(usuario => {
      // Filtro de texto
      const searchMatch = !searchTerm || 
        usuario.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        usuario.apellido?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        usuario.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        usuario.cedula?.includes(searchTerm) ||
        getEmpresaNombre(usuario.empresaId).toLowerCase().includes(searchTerm.toLowerCase());

      // Filtro por empresa
      const empresaMatch = !filterEmpresa || usuario.empresaId === filterEmpresa;

      // Filtro por rol
      const rolMatch = !filterRol || usuario.rol === filterRol;

      return searchMatch && empresaMatch && rolMatch;
    });

    return filtered;
  };

  // Agrupar usuarios según el criterio seleccionado
  const getGroupedUsuarios = () => {
    const filtered = getFilteredUsuarios();
    
    if (groupBy === "empresa") {
      const grouped = {};
      filtered.forEach(usuario => {
        const empresaNombre = getEmpresaNombre(usuario.empresaId);
        if (!grouped[empresaNombre]) {
          grouped[empresaNombre] = [];
        }
        grouped[empresaNombre].push(usuario);
      });
      return grouped;
    }
    
    if (groupBy === "rol") {
      const grouped = {};
      filtered.forEach(usuario => {
        const rolDisplay = rolesOptions.find(r => r.value === usuario.rol)?.label || usuario.rol;
        if (!grouped[rolDisplay]) {
          grouped[rolDisplay] = [];
        }
        grouped[rolDisplay].push(usuario);
      });
      return grouped;
    }
    
    return { "Todos los usuarios": filtered };
  };

  // Limpiar filtros
  const clearFilters = () => {
    setSearchTerm("");
    setFilterEmpresa("");
    setFilterRol("");
    setGroupBy("none");
  };

  // Guardar usuario
  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editingUsuario) {
        // Actualizar usuario existente
        const updateData = { ...formData };
        delete updateData.password; // No actualizar password en edición
        
        await updateDoc(doc(db, "usuarios", editingUsuario.id), {
          ...updateData,
          fechaModificacion: new Date()
        });
      } else {
        // Verificar si es rol administrativo
        const esRolAdministrativo = rolesAdministrativos.includes(formData.rol);
        
        if (esRolAdministrativo) {
          // Crear usuario administrativo con Firebase Auth
          if (!formData.password || formData.password.length < 6) {
            alert("Los usuarios administrativos requieren una contraseña de al menos 6 caracteres");
            return;
          }

          // Crear usuario en Firebase Auth
          const userCredential = await createUserWithEmailAndPassword(
            auth, 
            formData.email, 
            formData.password
          );

          // Guardar datos adicionales en Firestore
          await addDoc(collection(db, "usuarios"), {
            uid: userCredential.user.uid,
            nombre: formData.nombre,
            apellido: formData.apellido,
            email: formData.email,
            telefono: formData.telefono,
            rol: formData.rol,
            empresaId: formData.empresaId,
            estado: formData.estado,
            tipoAuth: 'firebase',
            fechaCreacion: new Date(),
            fechaModificacion: new Date()
          });
        } else {
          // Crear usuario empleado (sin Firebase Auth)
          if (!formData.cedula) {
            alert("Los empleados requieren número de cédula");
            return;
          }

          // Verificar que la cédula no esté duplicada
          const usuariosSnapshot = await getDocs(collection(db, "usuarios"));
          const existeCedula = usuariosSnapshot.docs.some(doc => 
            doc.data().cedula === formData.cedula
          );

          if (existeCedula) {
            alert("Ya existe un empleado con esta cédula");
            return;
          }

          // Guardar empleado solo en Firestore
          await addDoc(collection(db, "usuarios"), {
            nombre: formData.nombre,
            apellido: formData.apellido,
            cedula: formData.cedula,
            telefono: formData.telefono,
            rol: formData.rol,
            empresaId: formData.empresaId,
            estado: formData.estado,
            tipoAuth: 'cedula',
            fechaCreacion: new Date(),
            fechaModificacion: new Date()
          });
        }
      }
      
      setShowForm(false);
      setEditingUsuario(null);
      resetForm();
      loadData();
    } catch (error) {
      console.error("Error al guardar usuario:", error);
      if (error.code === 'auth/email-already-in-use') {
        alert("Este email ya está registrado en el sistema.");
      } else if (error.code === 'auth/weak-password') {
        alert("La contraseña es muy débil. Debe tener al menos 6 caracteres.");
      } else {
        alert("Error al guardar el usuario. Por favor intenta nuevamente.");
      }
    }
  };

  // Resetear formulario
  const resetForm = () => {
    setFormData({
      nombre: "",
      apellido: "",
      email: "",
      password: "",
      cedula: "",
      telefono: "",
      rol: "evaluador",
      empresaId: "",
      estado: "activo"
    });
  };

  // Editar usuario
  const handleEdit = (usuario) => {
    setEditingUsuario(usuario);
    setFormData({
      nombre: usuario.nombre,
      apellido: usuario.apellido,
      email: usuario.email || "",
      password: "", // No mostrar password
      cedula: usuario.cedula || "",
      telefono: usuario.telefono || "",
      rol: usuario.rol,
      empresaId: usuario.empresaId || "",
      estado: usuario.estado
    });
    setShowForm(true);
  };

  // Eliminar usuario
  const handleDelete = async (usuarioId) => {
    if (window.confirm("¿Estás seguro de que deseas eliminar este usuario?")) {
      try {
        await deleteDoc(doc(db, "usuarios", usuarioId));
        loadData();
      } catch (error) {
        console.error("Error al eliminar usuario:", error);
        alert("Error al eliminar el usuario.");
      }
    }
  };

  // Cancelar
  const handleCancel = () => {
    setShowForm(false);
    setEditingUsuario(null);
    resetForm();
  };

  const rolesOptions = [
    { value: "superadmin", label: "Super Administrador" },
    { value: "admin", label: "Administrador" },
    { value: "rrhh", label: "RRHH" },
    { value: "psicologo", label: "Psicólogo" },
    { value: "supervisor", label: "Supervisor" },
    { value: "evaluador", label: "Evaluador" }
  ];

  // Roles que requieren Firebase Auth (administradores)
  const rolesAdministrativos = ['superadmin', 'admin', 'rrhh', 'psicologo'];

  if (loading) {
    return (
      <DashboardBase title="Gestión de Usuarios" activeNav="usuarios">
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '200px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #1e40af',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <span style={{ marginLeft: '16px', color: '#64748b' }}>
            Cargando usuarios...
          </span>
        </div>
      </DashboardBase>
    );
  }

  return (
    <DashboardBase title="Gestión de Usuarios" activeNav="usuarios">
      <div className="content-area">
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <div>
            <h2 style={{ fontSize: '1.5em', fontWeight: '600', marginBottom: '8px' }}>
              Usuarios del Sistema
            </h2>
            <p style={{ color: '#64748b' }}>
              Gestiona los usuarios que tienen acceso al sistema de evaluación
            </p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="btn btn-primary"
            style={{
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: '600'
            }}
          >
            ➕ Nuevo Usuario
          </button>
        </div>

        {/* Panel de Filtros y Búsqueda */}
        <div className="card" style={{ marginBottom: '24px' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '16px',
            alignItems: 'end' 
          }}>
            {/* Búsqueda */}
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">🔍 Buscar usuarios</label>
              <input
                type="text"
                className="form-input"
                placeholder="Nombre, email, cédula o empresa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Filtro por Empresa */}
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">🏢 Filtrar por empresa</label>
              <select
                className="form-input"
                value={filterEmpresa}
                onChange={(e) => setFilterEmpresa(e.target.value)}
              >
                <option value="">Todas las empresas</option>
                {empresas.map(empresa => (
                  <option key={empresa.id} value={empresa.id}>
                    {empresa.nombre}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtro por Rol */}
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">🏷️ Filtrar por rol</label>
              <select
                className="form-input"
                value={filterRol}
                onChange={(e) => setFilterRol(e.target.value)}
              >
                <option value="">Todos los roles</option>
                {rolesOptions.map(role => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Agrupar por */}
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">📊 Agrupar por</label>
              <select
                className="form-input"
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
              >
                <option value="none">Sin agrupar</option>
                <option value="empresa">Por empresa</option>
                <option value="rol">Por rol</option>
              </select>
            </div>

            {/* Botón limpiar filtros */}
            <div>
              <button
                onClick={clearFilters}
                className="btn btn-outline"
                style={{ width: '100%' }}
              >
                🧹 Limpiar filtros
              </button>
            </div>
          </div>

          {/* Estadísticas de filtrado */}
          <div style={{ 
            marginTop: '16px', 
            padding: '12px 16px', 
            backgroundColor: '#f8fafc', 
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ color: '#64748b', fontSize: '0.9em' }}>
              Mostrando {getFilteredUsuarios().length} de {usuarios.length} usuarios
            </span>
            {(searchTerm || filterEmpresa || filterRol) && (
              <span style={{ 
                color: '#1e40af', 
                fontSize: '0.9em', 
                fontWeight: '500' 
              }}>
                📌 Filtros activos
              </span>
            )}
          </div>
        </div>

        {/* Formulario */}
        {showForm && (
          <div className="card" style={{ marginBottom: '24px' }}>
            <div className="card-header">
              <h3 className="card-title">
                {editingUsuario ? 'Editar Usuario' : 'Nuevo Usuario'}
              </h3>
            </div>
            <form onSubmit={handleSave}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                gap: '20px',
                marginBottom: '24px'
              }}>
                <div className="form-group">
                  <label className="form-label">Nombre</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.nombre}
                    onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                    required
                    placeholder="Nombre del usuario"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Apellido</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.apellido}
                    onChange={(e) => setFormData({...formData, apellido: e.target.value})}
                    required
                    placeholder="Apellido del usuario"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Rol</label>
                  <select
                    className="form-input"
                    value={formData.rol}
                    onChange={(e) => setFormData({...formData, rol: e.target.value})}
                    required
                  >
                    {rolesOptions.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Campos condicionales según el tipo de rol */}
                {rolesAdministrativos.includes(formData.rol) ? (
                  // Campos para usuarios administrativos
                  <>
                    <div className="form-group">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className="form-input"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                        disabled={editingUsuario}
                        placeholder="usuario@email.com"
                      />
                    </div>
                    
                    {!editingUsuario && (
                      <div className="form-group">
                        <label className="form-label">Contraseña</label>
                        <input
                          type="password"
                          className="form-input"
                          value={formData.password}
                          onChange={(e) => setFormData({...formData, password: e.target.value})}
                          required={!editingUsuario}
                          placeholder="Mínimo 6 caracteres"
                          minLength="6"
                        />
                      </div>
                    )}
                  </>
                ) : (
                  // Campo para empleados (solo cédula)
                  <div className="form-group">
                    <label className="form-label">Cédula</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.cedula}
                      onChange={(e) => setFormData({...formData, cedula: e.target.value})}
                      required
                      placeholder="Número de cédula (será su usuario y contraseña)"
                    />
                    <small style={{ color: '#64748b', fontSize: '0.8em' }}>
                      La cédula será utilizada como usuario y contraseña para el empleado
                    </small>
                  </div>
                )}
                
                <div className="form-group">
                  <label className="form-label">Teléfono</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={formData.telefono}
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                    placeholder="999-999-999"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Empresa</label>
                  <select
                    className="form-input"
                    value={formData.empresaId}
                    onChange={(e) => setFormData({...formData, empresaId: e.target.value})}
                  >
                    <option value="">Seleccionar empresa</option>
                    {empresas.map(empresa => (
                      <option key={empresa.id} value={empresa.id}>
                        {empresa.nombre}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Estado</label>
                  <select
                    className="form-input"
                    value={formData.estado}
                    onChange={(e) => setFormData({...formData, estado: e.target.value})}
                  >
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                  </select>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="submit" className="btn btn-primary">
                  {editingUsuario ? 'Actualizar' : 'Crear'} Usuario
                </button>
                <button 
                  type="button" 
                  onClick={handleCancel}
                  className="btn btn-outline"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Lista de usuarios */}
        {usuarios.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '48px' }}>
            <div style={{ fontSize: '3em', marginBottom: '16px' }}>👥</div>
            <h3 style={{ marginBottom: '8px' }}>No hay usuarios registrados</h3>
            <p style={{ color: '#64748b', marginBottom: '24px' }}>
              Comienza agregando el primer usuario al sistema
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="btn btn-primary"
            >
              Agregar Primer Usuario
            </button>
          </div>
        ) : getFilteredUsuarios().length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '48px' }}>
            <div style={{ fontSize: '3em', marginBottom: '16px' }}>🔍</div>
            <h3 style={{ marginBottom: '8px' }}>No se encontraron usuarios</h3>
            <p style={{ color: '#64748b', marginBottom: '24px' }}>
              No hay usuarios que coincidan con los filtros aplicados
            </p>
            <button
              onClick={clearFilters}
              className="btn btn-outline"
            >
              Limpiar filtros
            </button>
          </div>
        ) : (
          <div>
            {Object.entries(getGroupedUsuarios()).map(([groupName, groupUsuarios]) => (
              <div key={groupName} style={{ marginBottom: '32px' }}>
                {groupBy !== "none" && (
                  <div style={{
                    marginBottom: '16px',
                    padding: '12px 16px',
                    backgroundColor: '#f1f5f9',
                    borderRadius: '8px',
                    borderLeft: '4px solid #1e40af'
                  }}>
                    <h3 style={{
                      fontSize: '1.1em',
                      fontWeight: '600',
                      color: '#1e293b',
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      {groupBy === "empresa" ? "🏢" : "🏷️"} {groupName}
                      <span style={{
                        fontSize: '0.8em',
                        fontWeight: '500',
                        color: '#64748b',
                        backgroundColor: '#e2e8f0',
                        padding: '2px 8px',
                        borderRadius: '12px'
                      }}>
                        {groupUsuarios.length} usuario{groupUsuarios.length !== 1 ? 's' : ''}
                      </span>
                    </h3>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {groupUsuarios.map((usuario) => (
                    <div key={usuario.id} className="card">
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '16px'
                      }}>
                        <div style={{ flex: 1 }}>
                          <h3 style={{ fontSize: '1.2em', fontWeight: '600', marginBottom: '4px' }}>
                            {usuario.nombre} {usuario.apellido}
                          </h3>
                          <p style={{ color: '#64748b', fontSize: '0.9em' }}>
                            {usuario.email || `Cédula: ${usuario.cedula}`}
                          </p>
                          {usuario.tipoAuth && (
                            <span style={{
                              fontSize: '0.75em',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              backgroundColor: usuario.tipoAuth === 'firebase' ? '#dbeafe' : '#fef3c7',
                              color: usuario.tipoAuth === 'firebase' ? '#1e40af' : '#d97706'
                            }}>
                              {usuario.tipoAuth === 'firebase' ? 'Admin' : 'Empleado'}
                            </span>
                          )}
                        </div>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '0.8em',
                          fontWeight: '500',
                          backgroundColor: usuario.estado === 'activo' ? '#dcfce7' : '#fee2e2',
                          color: usuario.estado === 'activo' ? '#166534' : '#991b1b'
                        }}>
                          {usuario.estado}
                        </span>
                      </div>

                      <div style={{ marginBottom: '16px' }}>
                        <p style={{ color: '#64748b', fontSize: '0.9em', marginBottom: '4px' }}>
                          🏷️ Rol: {rolesOptions.find(r => r.value === usuario.rol)?.label || usuario.rol}
                        </p>
                        
                        {usuario.empresaId && (
                          <p style={{ color: '#64748b', fontSize: '0.9em', marginBottom: '4px' }}>
                            🏢 {getEmpresaNombre(usuario.empresaId)}
                          </p>
                        )}
                        
                        {usuario.telefono && (
                          <p style={{ color: '#64748b', fontSize: '0.9em', marginBottom: '4px' }}>
                            📞 {usuario.telefono}
                          </p>
                        )}
                      </div>

                      <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                        <button
                          onClick={() => handleEdit(usuario)}
                          className="btn btn-outline"
                          style={{ flex: 1, fontSize: '0.9em' }}
                        >
                          ✏️ Editar
                        </button>
                        <button
                          onClick={() => handleDelete(usuario.id)}
                          className="btn btn-danger"
                          style={{ flex: 1, fontSize: '0.9em' }}
                        >
                          🗑️ Eliminar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardBase>
  );
};

export default UsuariosManager;
